import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListAgentsResult, type ListPrebuiltAgentsResult } from '@whinerp/client';
import { Button, EmptyState, ErrorState, Panel, SectionLabel, Skeleton, type Column } from '@whinerp/ui';
import { useNavigate } from 'react-router-dom';
import { useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-54 Agents list.
 *
 * The agent builder was reachable only by id, so the only way to an agent
 * was a link from somewhere else. This lists them with their status, which
 * is the thing worth seeing at a glance: a draft agent does nothing and an
 * active one is acting on the workspace right now.
 */

type Row = ListAgentsResult['agents'][number];

function AgentsListScreen(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({ field: 'name', direction: 'asc' });
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const query = useQuery<ListAgentsResult, ApiError>({
    queryKey: ['agents'],
    queryFn: () => api.listAgents({}),
  });

  /*
   * The ready-made agents (F-116 to F-123).
   *
   * Beside the list rather than behind a menu: "write an agent from nothing"
   * is a blank page, and the eight that come with the product are the answer
   * to what one is supposed to look like.
   */
  const queryClient = useQueryClient();
  const prebuilt = useQuery<ListPrebuiltAgentsResult, ApiError>({
    queryKey: ['agents-prebuilt'],
    queryFn: () => api.listPrebuiltAgents({}),
  });
  const install = useMutation({
    mutationFn: (key: string) => api.installPrebuiltAgent({ path: { key }, body: {} }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['agents'] });
      await queryClient.invalidateQueries({ queryKey: ['agents-prebuilt'] });
      // Straight into the builder: it arrived switched off and the next thing
      // anybody should do is read what it says before switching it on.
      navigate(`/agents/${result.agentId}`);
    },
  });

  /*
   * Narrowed and ordered here rather than by the server.
   *
   * A workspace has tens of these, not tens of thousands. Paging them would
   * mean a cursor, a total and a page size describing a list that fits on one
   * screen - and the toolbar's search is honest about what it does: it
   * narrows what is already loaded.
   */
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = query.data?.agents ?? [];
    const found = term === '' ? all : all.filter((row) => row.name.toLowerCase().includes(term) || row.purpose.toLowerCase().includes(term));
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...found].sort((a, b) => sort.field === 'status'
        ? a.status.localeCompare(b.status) * direction
        : a.name.localeCompare(b.name) * direction);
  }, [query.data, search, sort]);

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('list.column.name'),
        sortField: 'name',
        essential: true,
        cell: (row) => row.name,
      },
      {
        key: 'status',
        header: t('list.column.status'),
        sortField: 'status',
        essential: true,
        cell: (row) => row.status,
      },
      {
        key: 'purpose',
        header: t('list.column.purpose'),
        hideOnCard: true,
        cell: (row) => row.purpose,
      },
    ],
    [t],
  );

  return (
    <div className="flex w-full flex-col gap-4">
    <RecordList<Row>
      title={t('list.agents.title')}
      icon="Sparkle"
      rowId={(row) => row.agentId}
      recordHref={(row) => `/agents/${row.agentId}`}
      columns={columns}
      page={{ rows, total: rows.length, hasMore: false, nextCursor: null }}
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={setSearch}
      sort={sort}
      onSortChange={(field) => setSort((current) => nextSort(current, field))}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={rows.length || 1}
      onNew={() => navigate('/agents/new')}
      onClearFilters={() => setSearch('')}
      filtered={search.trim().length > 0}
      emptyTitle={t('list.agents.empty.title')}
      emptyBody={t('list.agents.empty.body')}
      emptyIllustration="Robot"
    />

      {prebuilt.data ? (
        <Panel data-region="prebuilt-agents">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-0.5">
              <SectionLabel>{t('agent.prebuilt.title')}</SectionLabel>
              <p className="text-meta text-ink-meta">{t('agent.prebuilt.body')}</p>
            </div>
            <ul className="flex flex-col gap-1">
              {prebuilt.data.prebuilt.map((entry) => (
                <li
                  key={entry.key}
                  data-prebuilt={entry.key}
                  className="flex flex-wrap items-baseline gap-2 border-t border-t-rule pt-1 first:border-t-0 first:pt-0"
                >
                  <span className="text-dense text-ink">{entry.name}</span>
                  {/* The purpose, always: it is what somebody reads before
                      deciding whether this should touch their records. */}
                  <span className="min-w-0 flex-1 text-meta text-ink-meta">{entry.purpose}</span>
                  {entry.installed ? (
                    <a data-prebuilt-open={entry.key} className="text-meta text-bronze-700 underline" href={`/agents/${entry.installedAgentId ?? ''}`}>
                      {t('agent.prebuilt.installed')}
                    </a>
                  ) : (
                    <Button
                      intent="secondary"
                      size="small"
                      data-prebuilt-install={entry.key}
                      disabled={install.isPending}
                      onClick={() => install.mutate(entry.key)}
                    >
                      {t('agent.prebuilt.install')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      ) : null}
    </div>
  );
}

function AgentsListEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Sparkle"
      title={t('list.agents.empty.title')}
      body={t('list.agents.empty.body')}
      primary={{ label: t('agent.new.create'), icon: 'Plus', href: '/agents/new' }}
    />
  );
}

function AgentsListLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading</span>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function AgentsListError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Agents could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-54',
  route: '/agents',
  surface: 'page',
  title: 'Agents list',
  features: ['F-105'],
  entitlement: 'F-105',
  reachedBy: [{ kind: 'nav', item: 'agents' }],
  components: ['DataTable', 'FilterChips'],
  elements: ['RecordList'],
  states: {
    empty: AgentsListEmpty,
    loading: AgentsListLoading,
    error: AgentsListError,
  },
  component: AgentsListScreen,
});

export default screen;
